import { Box, Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import PanesHeader from 'modules/core/components/PanesHeader'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import QuestionConfigs from 'modules/surveys/components/Form/QuestionConfigs/QuestionConfigs'
import questionCategories from 'modules/surveys/store/collections/questionCategories'
import questionGroupConfigsCollection from 'modules/surveys/store/collections/questionGroupConfigs'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  form: SurveyForm | Survey
}

export default function SurveyQuestions({ form }: Props) {
  const { navigation } = useNavigation()

  const [isOpen, toggleOpen] = useToggle(false)
  const formId = form.attributes.id

  const questionGroupConfig = questionGroupConfigsCollection.find({
    surveys_form_id: formId,
  })

  const questionGroupConfigId = questionGroupConfig?.attributes.id

  const { loading } = useAsyncData(async () => {
    if (!formId) return

    return Promise.all([
      questionGroupConfigsCollection.fetch({
        data: {
          surveys_form_id: formId,
        },
      }),
      questionCategories.fetch({
        data: {
          surveys_form_id: formId,
        },
      }),
    ])
  }, [formId])

  if (loading) return <Loading />

  if (!questionGroupConfig) return null

  return (
    <PanesPad>
      <Panes direction="horizontal-2-cols-left-1-third" type="naked">
        <Box>
          <PanesHeader
            separation
            illustration="tasks_management"
            title={i18n.t('surveys.form.question_configs.title')}
            description={i18n.t('surveys.form.question_configs.description')}
          />
          <Box flexDirection="column" gap="s16">
            <Button.Router
              type="primary"
              to={navigation.processes.surveys.id.questions.questionGroupConfigIdAddQuestion.fullPath(
                { id: formId, question_group_config_id: questionGroupConfigId }
              )}
            >
              {i18n.t('surveys.form.question_configs.add_question_config')}
            </Button.Router>
            <Box flexDirection="row" gap="s16">
              <Button.Router
                type="secondary"
                to={navigation.processes.surveys.id.questions.questionGroupConfigIdAddCategory.fullPath(
                  { id: formId, question_group_config_id: questionGroupConfigId }
                )}
                iconName="add"
              >
                {i18n.t('surveys.form.question_configs.add_section')}
              </Button.Router>
              <Tooltip.Alt
                attachment="top center"
                open={isOpen}
                toggle={toggleOpen}
                label={i18n.t('surveys.form.question_configs.import_template')}
              >
                <Button.Router
                  type="secondary"
                  to={navigation.processes.surveys.id.questions.questionGroupConfigIdApplyTemplate.fullPath(
                    { id: formId, question_group_config_id: questionGroupConfigId }
                  )}
                  iconName="upload"
                />
              </Tooltip.Alt>
            </Box>
          </Box>
        </Box>
        <Box paddingLeft="s32" gap="s40">
          <QuestionConfigs questionGroupConfig={questionGroupConfig} form={form} />
        </Box>
      </Panes>
    </PanesPad>
  )
}
